import { useEffect, useMemo, useState } from 'react';
import {
  fetchSevenDayForecast,
  fetchWeather,
  geocodeZip,
  weatherTipKind,
  type ForecastDay,
  type WeatherCondition,
  type WeatherSnapshot,
  type WeatherTipKind,
} from '../../lib/weather';
import { useRecentVisits } from '@busy/api';
import { ModalShell } from '@busy/ui';
import { useUI } from '../../store/ui';
import { useT } from '../../lib/i18n';
import {
  dayOfWeekPattern,
  dowFromIsoDate,
  type DayOfWeekIntensity,
} from '../../lib/dayOfWeekPattern';

// Compact pill — date · location · temp · icon · condition · (alert badge).
// Tapping it does nothing in v1; in v1.1 we'll route to the Weather Contingency lesson
// like the demo does.

const TILE_BG: Record<WeatherCondition, string> = {
  sunny: 'bg-gradient-to-br from-amber-500 to-amber-600 text-white',
  'partly-cloudy': 'bg-gradient-to-br from-blue-400 to-blue-500 text-white',
  cloudy: 'bg-gradient-to-br from-slate-400 to-slate-500 text-white',
  rainy: 'bg-gradient-to-br from-blue-500 to-blue-700 text-white',
  storm: 'bg-gradient-to-br from-slate-600 to-slate-800 text-white border border-red-500/40',
  snow: 'bg-gradient-to-br from-blue-200 to-blue-400 text-navy-900',
  fog: 'bg-gradient-to-br from-slate-300 to-slate-400 text-white',
};

export function WeatherTile({ zip }: { zip: string | null }) {
  const [data, setData] = useState<WeatherSnapshot | null>(null);
  const [loading, setLoading] = useState(false);
  const [forecastOpen, setForecastOpen] = useState(false);
  const [forecast, setForecast] = useState<ForecastDay[] | null>(null);
  const [forecastLoading, setForecastLoading] = useState(false);
  const language = useUI((s) => s.language);
  const t = useT();

  useEffect(() => {
    if (!zip) {
      setData(null);
      return;
    }
    let alive = true;
    setLoading(true);
    (async () => {
      const geo = await geocodeZip(zip);
      if (!alive || !geo) {
        if (alive) setLoading(false);
        return;
      }
      const snap = await fetchWeather(geo);
      if (!alive) return;
      setData(snap);
      setLoading(false);
    })();
    return () => {
      alive = false;
    };
  }, [zip]);

  // Fetch the 7-day forecast lazily on first open. Cached at the lib layer
  // for an hour, so reopening within that window is instant.
  async function openForecast() {
    setForecastOpen(true);
    if (forecast || !zip) return;
    setForecastLoading(true);
    const geo = await geocodeZip(zip);
    if (!geo) {
      setForecastLoading(false);
      return;
    }
    const days = await fetchSevenDayForecast(geo);
    setForecast(days);
    setForecastLoading(false);
  }

  if (loading && !data) {
    return <div className="inline-block h-8 w-48 rounded-full bg-bg-alt animate-pulse" />;
  }
  if (!data) {
    if (!zip) return null;
    return null;
  }

  // Date format follows the active UI language — Spanish locale gives
  // "miércoles, may 4" instead of "Wednesday, May 4".
  const dateLocale = language === 'es' ? 'es-US' : 'en-US';
  const dateStr = new Date().toLocaleDateString(dateLocale, {
    weekday: 'long',
    month: 'short',
    day: 'numeric',
  });
  const conditionLabel = t(data.labelKey);

  return (
    <>
      <button
        type="button"
        onClick={openForecast}
        aria-label={t('weather.openForecastAria', {
          date: dateStr,
          location: data.locationLabel,
          temp: data.tempF,
          condition: conditionLabel,
        })}
        className={`inline-flex items-center gap-2 rounded-full pl-3 pr-3 py-1.5 text-xs shadow-sm hover:-translate-y-0.5 hover:shadow-md transition-transform cursor-pointer ${TILE_BG[data.condition]}`}
      >
        <span className="font-bold opacity-95">{dateStr}</span>
        <span className="text-[10.5px] opacity-75 inline-flex items-center gap-1">
          <MapPinIcon className="w-2.5 h-2.5" />
          {data.locationLabel}
        </span>
        <span className="opacity-35">·</span>
        <span className="font-display text-sm tabular-nums">{data.tempF}°</span>
        <div className="w-6 h-6 rounded-full bg-white/20 grid place-items-center shrink-0">
          <WeatherIcon condition={data.condition} />
        </div>
        <span className="text-[11px] font-semibold opacity-90">{conditionLabel}</span>
        {data.alert && (
          <span className="inline-flex items-center gap-1 bg-red-600 text-white text-[9px] font-extrabold tracking-[0.08em] uppercase py-0.5 pr-1.5 rounded-full" style={{ paddingLeft: 'calc(0.375rem + 0.08em)' }}>
            <AlertIcon className="w-2 h-2" />
            {t('weather.alert')}
          </span>
        )}
      </button>
      {forecastOpen && (
        <ForecastModal
          location={data.locationLabel}
          days={forecast}
          loading={forecastLoading}
          locale={dateLocale}
          onClose={() => setForecastOpen(false)}
        />
      )}
    </>
  );
}

function ForecastModal({
  location,
  days,
  loading,
  locale,
  onClose,
}: {
  location: string;
  days: ForecastDay[] | null;
  loading: boolean;
  locale: string;
  onClose: () => void;
}) {
  const t = useT();
  const [selected, setSelected] = useState<ForecastDay | null>(null);
  // Pull the last 90 days of visits to classify each weekday as peak / slow
  // / average for this barber. The tip view uses this to layer a personalized
  // capacity note on top of the weather playbook.
  const recentVisits = useRecentVisits(90);
  const dowIntensity = useMemo(
    () => dayOfWeekPattern(recentVisits.data),
    [recentVisits.data],
  );
  return (
    <ModalShell open onClose={onClose} size="md" ariaLabel={t('weather.forecast.title')}>
      <div className="p-5 md:p-6 bg-surface rounded-2xl">
        {selected ? (
          <WeatherTipView
            day={selected}
            locale={locale}
            location={location}
            dowIntensity={dowIntensity[dowFromIsoDate(selected.date)]}
            onBack={() => setSelected(null)}
            onClose={onClose}
          />
        ) : (
          <>
            <div className="text-[11px] tracking-[0.16em] uppercase font-extrabold text-gold-500 mb-1">
              {t('weather.forecast.eyebrow')}
            </div>
            <h2 className="font-display text-2xl md:text-3xl leading-tight mb-1">
              {t('weather.forecast.title')}
            </h2>
            <p className="text-sm text-ink-500 mb-4 inline-flex items-center gap-1.5">
              <MapPinIcon className="w-3 h-3" />
              {location}
            </p>
            {loading || !days ? (
              <ul className="space-y-2">
                {Array.from({ length: 7 }).map((_, i) => (
                  <li key={i} className="h-12 rounded-xl bg-bg-alt animate-pulse" />
                ))}
              </ul>
            ) : (
              <ul className="space-y-1.5">
                {days.map((d, i) => {
                  const date = new Date(d.date + 'T00:00:00');
                  const labelTop =
                    i === 0
                      ? t('weather.forecast.today')
                      : date.toLocaleDateString(locale, { weekday: 'long' });
                  const labelDate = date.toLocaleDateString(locale, { month: 'short', day: 'numeric' });
                  return (
                    <li key={d.date}>
                      <button
                        type="button"
                        onClick={() => setSelected(d)}
                        aria-label={t('weather.forecast.openTipAria', { day: `${labelTop}, ${labelDate}` })}
                        className="w-full flex items-center gap-3 rounded-xl border border-line bg-bg-alt px-3 py-2.5 text-left hover:bg-bg-alt/80 hover:border-gold-500/40 active:scale-[0.99] transition-all cursor-pointer"
                      >
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-bold leading-tight">{labelTop}</div>
                          <div className="text-[11px] text-ink-500">{labelDate}</div>
                        </div>
                        <div
                          className={`w-9 h-9 rounded-full grid place-items-center shrink-0 ${TILE_BG[d.condition]}`}
                        >
                          <WeatherIcon condition={d.condition} />
                        </div>
                        <div className="text-sm shrink-0 text-right tabular-nums">
                          <span className="font-bold">{d.highF}°</span>
                          <span className="text-ink-500"> / {d.lowF}°</span>
                        </div>
                        {d.alert && (
                          <span className="shrink-0 inline-flex items-center gap-1 bg-red-600 text-white text-[9px] font-extrabold tracking-[0.08em] uppercase py-0.5 pr-1.5 rounded-full" style={{ paddingLeft: 'calc(0.375rem + 0.08em)' }}>
                            <AlertIcon className="w-2 h-2" />
                            {t('weather.alert')}
                          </span>
                        )}
                        <ChevronRightIcon className="w-4 h-4 text-ink-500 shrink-0" />
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
            <button
              type="button"
              onClick={onClose}
              className="mt-5 w-full h-11 rounded-lg bg-navy-900 text-white dark:bg-gold-500 dark:text-navy-900 font-bold"
            >
              {t('weather.forecast.close')}
            </button>
          </>
        )}
      </div>
    </ModalShell>
  );
}

// Tip detail — picks a kind via weatherTipKind(day) and renders the matching
// 3-bullet playbook. Header echoes the tapped day so the user keeps context
// after they leave the list.
function WeatherTipView({
  day,
  locale,
  location,
  dowIntensity,
  onBack,
  onClose,
}: {
  day: ForecastDay;
  locale: string;
  location: string;
  /** Whether this date typically falls on a peak / slow / average day for
   *  the barber based on their visit history. Drives the personalized
   *  capacity callout above the playbook. */
  dowIntensity: DayOfWeekIntensity;
  onBack: () => void;
  onClose: () => void;
}) {
  const t = useT();
  const kind: WeatherTipKind = weatherTipKind(day, dowIntensity);
  const date = new Date(day.date + 'T00:00:00');
  const dayLabel = date.toLocaleDateString(locale, {
    weekday: 'long',
    month: 'short',
    day: 'numeric',
  });
  return (
    <>
      <button
        type="button"
        onClick={onBack}
        className="text-[11px] tracking-[0.14em] uppercase font-extrabold text-ink-500 inline-flex items-center gap-1.5 mb-2 hover:text-ink-700 transition-colors"
      >
        <ChevronLeftIcon className="w-3 h-3" />
        {t('weather.tip.back')}
      </button>
      <div className="text-[11px] tracking-[0.16em] uppercase font-extrabold text-gold-500 mb-1">
        {t('weather.tip.eyebrow')}
      </div>
      <h2 className="font-display text-2xl md:text-3xl leading-tight mb-1">
        {t(`weather.tip.${kind}.title`)}
      </h2>
      <p className="text-sm text-ink-500 mb-3 inline-flex items-center gap-1.5">
        <span>{dayLabel}</span>
        <span className="opacity-50">·</span>
        <MapPinIcon className="w-3 h-3" />
        <span>{location}</span>
      </p>

      {/* Day summary chip — visual anchor for which day's tip you're reading. */}
      <div className={`flex items-center gap-3 rounded-xl px-3 py-2.5 mb-4 ${TILE_BG[day.condition]}`}>
        <div className="w-8 h-8 rounded-full bg-white/20 grid place-items-center shrink-0">
          <WeatherIcon condition={day.condition} />
        </div>
        <div className="flex-1 min-w-0 text-sm font-semibold">
          {t(day.labelKey)}
        </div>
        <div className="text-sm shrink-0 text-right tabular-nums">
          <span className="font-bold">{day.highF}°</span>
          <span className="opacity-75"> / {day.lowF}°</span>
        </div>
        {day.alert && (
          <span className="shrink-0 inline-flex items-center gap-1 bg-red-600 text-white text-[9px] font-extrabold tracking-[0.08em] uppercase py-0.5 pr-1.5 rounded-full" style={{ paddingLeft: 'calc(0.375rem + 0.08em)' }}>
            <AlertIcon className="w-2 h-2" />
            {t('weather.alert')}
          </span>
        )}
      </div>

      <p className="text-[15px] italic text-ink-700 mb-4 leading-snug">
        {t(`weather.tip.${kind}.tagline`)}
      </p>

      <div className="text-[11px] tracking-[0.14em] uppercase font-extrabold text-ink-500 mb-2">
        {t('weather.tip.bulletsHeading')}
      </div>
      <ol className="space-y-2.5 mb-5">
        {[1, 2, 3].map((n) => (
          <li key={n} className="flex gap-2.5">
            <span className="mt-0.5 w-5 h-5 shrink-0 rounded-full bg-navy-900 dark:bg-gold-500 text-white dark:text-navy-900 text-[10px] font-extrabold grid place-items-center">
              {n}
            </span>
            <p className="text-sm leading-snug">{t(`weather.tip.${kind}.b${n}`)}</p>
          </li>
        ))}
      </ol>

      <button
        type="button"
        onClick={onClose}
        className="w-full h-11 rounded-lg bg-navy-900 text-white dark:bg-gold-500 dark:text-navy-900 font-bold"
      >
        {t('weather.tip.close')}
      </button>
    </>
  );
}

function WeatherIcon({ condition }: { condition: WeatherCondition }) {
  const cls = 'w-3.5 h-3.5';
  switch (condition) {
    case 'sunny':
      return <SunIcon className={cls} />;
    case 'partly-cloudy':
      return <CloudSunIcon className={cls} />;
    case 'cloudy':
    case 'fog':
      return <CloudIcon className={cls} />;
    case 'rainy':
      return <RainIcon className={cls} />;
    case 'storm':
      return <StormIcon className={cls} />;
    case 'snow':
      return <SnowIcon className={cls} />;
  }
}

/* ============== icons (inlined to avoid icon-library dep) ============== */
function MapPinIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.25" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
      <circle cx="12" cy="10" r="3" />
    </svg>
  );
}
function AlertIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
      <line x1="12" y1="9" x2="12" y2="13" />
      <line x1="12" y1="17" x2="12.01" y2="17" />
    </svg>
  );
}
function SunIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.25" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <circle cx="12" cy="12" r="4" />
      <line x1="12" y1="2" x2="12" y2="4" />
      <line x1="12" y1="20" x2="12" y2="22" />
      <line x1="4.93" y1="4.93" x2="6.34" y2="6.34" />
      <line x1="17.66" y1="17.66" x2="19.07" y2="19.07" />
      <line x1="2" y1="12" x2="4" y2="12" />
      <line x1="20" y1="12" x2="22" y2="12" />
      <line x1="4.93" y1="19.07" x2="6.34" y2="17.66" />
      <line x1="17.66" y1="6.34" x2="19.07" y2="4.93" />
    </svg>
  );
}
function CloudSunIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.25" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M12 2v2" />
      <path d="m4.93 4.93 1.41 1.41" />
      <path d="M20 12h2" />
      <path d="m19.07 4.93-1.41 1.41" />
      <path d="M15.947 12.65a4 4 0 0 0-5.925-4.128" />
      <path d="M13 22H7a5 5 0 1 1 4.9-6H13a3 3 0 0 1 0 6Z" />
    </svg>
  );
}
function CloudIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.25" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M17.5 19a4.5 4.5 0 1 0 0-9h-1.8A7 7 0 1 0 4 14.9" />
    </svg>
  );
}
function RainIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.25" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M4 14.899A7 7 0 1 1 15.71 8h1.79a4.5 4.5 0 0 1 2.5 8.242" />
      <path d="M16 14v6" />
      <path d="M8 14v6" />
      <path d="M12 16v6" />
    </svg>
  );
}
function StormIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.25" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M19 16.9A5 5 0 0 0 18 7h-1.26a8 8 0 1 0-11.62 9" />
      <polyline points="13 11 9 17 13 17 11 21" />
    </svg>
  );
}
function SnowIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.25" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M2 12h20" />
      <path d="M12 2v20" />
      <path d="m4.93 4.93 14.14 14.14" />
      <path d="m19.07 4.93-14.14 14.14" />
    </svg>
  );
}
function ChevronRightIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <polyline points="9 18 15 12 9 6" />
    </svg>
  );
}
function ChevronLeftIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <polyline points="15 18 9 12 15 6" />
    </svg>
  );
}
